import { useEffect, useState } from 'react';
import type { AttributeDto, AttributeType, CreateAttributeRequest } from '@ecom/contracts';
import { useAttributesStore } from '@/stores/attributes.store';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { PlusIcon, TagIcon, TrashIcon } from '@/components/icons';
import { ListCard, Page, Sheet, TableScroll } from '@/components/page';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  IconButton,
  Input,
  Pill,
  Select,
  TableSkeleton,
} from '@/components/ui';

/**
 * The specification library.
 *
 * Typed facts a product can carry — material, capacity, warranty — defined once
 * and reused. A category can then ask for a set of them, so every product in it
 * answers the same questions without anyone remembering to add the fields.
 *
 * These describe a product; they do not create purchasable variants. A variant
 * keeps its own options, which is what stock and price hang off.
 *
 * Adding and editing one opens in a side panel, so the library stays in view.
 */

const TYPES: AttributeType[] = ['TEXT', 'NUMBER', 'BOOLEAN', 'SELECT', 'MULTI_SELECT'];

export function AttributesPage() {
  const { library, loading, saving, error, load, deactivate } = useAttributesStore();
  const user = useAuthStore((state) => state.user);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [editing, setEditing] = useState<AttributeDto | 'new' | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  if (user?.role !== 'OWNER') {
    return (
      <Page title={t('attributes')}>
        <Card className="flex flex-1 flex-col">
          <EmptyState title={t('ownerOnlySettings')} icon={<TagIcon />} />
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title={t('attributes')}
      subtitle={t('attributesSubtitle')}
      actions={
        <Button onClick={() => setEditing('new')}>
          <PlusIcon className="size-4" />
          {t('addAttribute')}
        </Button>
      }
      fill
    >
      <ListCard>
        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && library.length === 0 ? (
          <TableSkeleton rows={5} columns={3} />
        ) : library.length === 0 ? (
          <EmptyState title={t('noAttributes')} body={t('noAttributesHint')} icon={<TagIcon />} />
        ) : (
          <TableScroll>
            <ul className="flex flex-col divide-y divide-line">
              {library.map((attribute) => (
                <li
                  key={attribute.id}
                  className="flex flex-wrap items-center gap-3 px-5 py-3.5 transition-colors hover:bg-surface-sunken/70"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-ink">
                      {attribute.translations[locale] || attribute.label}
                    </span>
                    <span data-numeric dir="ltr" className="block text-xs text-ink-faint">
                      {attribute.code}
                      {attribute.unit ? ` · ${attribute.unit}` : ''}
                    </span>
                  </span>

                  <Pill tone="neutral">{t(`attrType${attribute.type}` as 'attrTypeTEXT')}</Pill>

                  {attribute.options.length > 0 ? (
                    <span data-numeric className="text-xs text-ink-muted">
                      {attribute.options.length} {t('optionsCount')}
                    </span>
                  ) : null}

                  {attribute.isFilterable ? <Pill tone="info">{t('filterable')}</Pill> : null}
                  {!attribute.isActive ? <Pill tone="neutral">{t('retired')}</Pill> : null}

                  <span className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(attribute)}>
                      {t('editContent')}
                    </Button>
                    {attribute.isActive ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={saving}
                        onClick={() => void deactivate(attribute.id)}
                      >
                        {t('retire')}
                      </Button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </TableScroll>
        )}
      </ListCard>

      {editing ? (
        <AttributeSheet
          // Keyed by the attribute, so opening another one starts its form afresh.
          key={editing === 'new' ? 'new' : editing.id}
          attribute={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
        />
      ) : null}
    </Page>
  );
}

function AttributeSheet({
  attribute,
  onDone,
}: {
  attribute: AttributeDto | null;
  onDone: () => void;
}) {
  const { create, update, saving, error } = useAttributesStore();
  const t = useT();

  const [draft, setDraft] = useState<Draft>(() => toDraft(attribute));
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));

  const isChooser = draft.type === 'SELECT' || draft.type === 'MULTI_SELECT';

  return (
    <Sheet
      open
      size="lg"
      title={attribute ? t('editAttribute') : t('addAttribute')}
      subtitle={t('attributeEditorHint')}
      onClose={onDone}
      footer={
        <>
          {error ? (
            <span className="me-auto">
              <Alert>{error}</Alert>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            disabled={saving || draft.code.trim() === '' || draft.en.trim() === ''}
            onClick={async () => {
              const body = toRequest(draft);
              const ok = attribute
                ? await update(attribute.id, {
                    translations: body.translations,
                    unit: body.unit,
                    isFilterable: body.isFilterable,
                    options: body.options,
                  })
                : await create(body);

              if (ok) onDone();
            }}
          >
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('attributeCode')} htmlFor="attr-code" hint={t('attributeCodeHint')}>
            <Input
              id="attr-code"
              dir="ltr"
              value={draft.code}
              // A code is identity: changing it after products reference it
              // would orphan an import mapping, so it is set once.
              disabled={attribute !== null}
              onChange={(e) => set({ code: e.target.value })}
            />
          </Field>
          <Field label={t('attributeType')} htmlFor="attr-type">
            <Select
              id="attr-type"
              value={draft.type}
              disabled={attribute !== null}
              onChange={(e) => set({ type: e.target.value as AttributeType, options: [] })}
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`attrType${type}` as 'attrTypeTEXT')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('nameArabic')} htmlFor="attr-ar">
            <Input id="attr-ar" dir="rtl" value={draft.ar} onChange={(e) => set({ ar: e.target.value })} />
          </Field>
          <Field label={t('nameEnglish')} htmlFor="attr-en">
            <Input id="attr-en" dir="ltr" value={draft.en} onChange={(e) => set({ en: e.target.value })} />
          </Field>
          {draft.type === 'NUMBER' ? (
            <Field label={t('measurementUnit')} htmlFor="attr-unit" hint={t('unitHint')}>
              <Input id="attr-unit" dir="ltr" value={draft.unit} onChange={(e) => set({ unit: e.target.value })} />
            </Field>
          ) : null}
        </div>

        <label className="flex cursor-pointer items-start gap-3 rounded-control border border-line px-3 py-2.5 transition-colors hover:bg-surface-sunken">
          <input
            type="checkbox"
            checked={draft.isFilterable}
            onChange={(event) => set({ isFilterable: event.target.checked })}
            className="mt-0.5 size-4 shrink-0"
          />
          <span className="min-w-0">
            <span className="block text-sm text-ink">{t('filterable')}</span>
            <span className="block text-xs text-ink-muted">{t('filterableHint')}</span>
          </span>
        </label>

        {isChooser ? (
          <div className="border-t border-line pt-5">
            <h3 className="mb-1 text-sm font-medium text-ink">{t('choices')}</h3>
            <p className="mb-3 text-xs text-ink-muted">{t('choicesHint')}</p>

            <div className="flex flex-col gap-2">
              {draft.options.map((option, index) => (
                <div key={option.id ?? `new-${index}`} className="grid gap-2 sm:grid-cols-[8rem_1fr_1fr_auto]">
                  <Input
                    dir="ltr"
                    placeholder={t('attributeCode')}
                    value={option.code}
                    onChange={(event) =>
                      set({
                        options: draft.options.map((entry, position) =>
                          position === index ? { ...entry, code: event.target.value } : entry,
                        ),
                      })
                    }
                  />
                  <Input
                    dir="rtl"
                    placeholder={t('nameArabic')}
                    value={option.ar}
                    onChange={(event) =>
                      set({
                        options: draft.options.map((entry, position) =>
                          position === index ? { ...entry, ar: event.target.value } : entry,
                        ),
                      })
                    }
                  />
                  <Input
                    dir="ltr"
                    placeholder={t('nameEnglish')}
                    value={option.en}
                    onChange={(event) =>
                      set({
                        options: draft.options.map((entry, position) =>
                          position === index ? { ...entry, en: event.target.value } : entry,
                        ),
                      })
                    }
                  />
                  <IconButton
                    label={t('remove')}
                    // A saved choice may already be answered on products: say so before it goes.
                    title={option.id ? t('removeChoiceWarning') : undefined}
                    onClick={() =>
                      set({ options: draft.options.filter((_, position) => position !== index) })
                    }
                    className="border-0 hover:text-danger"
                  >
                    <TrashIcon className="size-4" />
                  </IconButton>
                </div>
              ))}
            </div>

            <div className="mt-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => set({ options: [...draft.options, { code: '', ar: '', en: '' }] })}
              >
                <PlusIcon className="size-4" />
                {t('addChoice')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

interface DraftOption {
  id?: string;
  code: string;
  ar: string;
  en: string;
}

interface Draft {
  code: string;
  ar: string;
  en: string;
  type: AttributeType;
  unit: string;
  isFilterable: boolean;
  options: DraftOption[];
}

function toDraft(attribute: AttributeDto | null): Draft {
  if (!attribute) {
    return { code: '', ar: '', en: '', type: 'TEXT', unit: '', isFilterable: false, options: [] };
  }

  return {
    code: attribute.code,
    ar: attribute.translations.ar,
    en: attribute.translations.en,
    type: attribute.type,
    unit: attribute.unit ?? '',
    isFilterable: attribute.isFilterable,
    options: attribute.options.map((option) => ({
      id: option.id,
      code: option.code,
      // Each box from its own language (ADR-053). Seeding both from `label` —
      // resolved in the UI's language — wrote that language over the other one
      // on every save; the label stands in only for an API that sends no
      // translations.
      ...(option.translations
        ? { ar: option.translations.ar ?? '', en: option.translations.en ?? '' }
        : { ar: option.label, en: option.label }),
    })),
  };
}

function toRequest(draft: Draft): CreateAttributeRequest {
  const isChooser = draft.type === 'SELECT' || draft.type === 'MULTI_SELECT';

  return {
    code: draft.code.trim(),
    translations: { ar: draft.ar.trim() || draft.en.trim(), en: draft.en.trim() },
    type: draft.type,
    unit: draft.type === 'NUMBER' ? draft.unit.trim() || null : null,
    isFilterable: draft.isFilterable,
    options: isChooser
      ? draft.options
          .filter((option) => option.code.trim() && option.en.trim())
          .map((option) => ({
            id: option.id,
            code: option.code.trim(),
            translations: { ar: option.ar.trim() || option.en.trim(), en: option.en.trim() },
          }))
      : undefined,
  };
}
